import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EXCEPTION_CATEGORY_HINTS,
  TRANSACTION_STATUSES,
  TRANSACTION_STATUS_LABELS,
  type ControlReviewCategory,
  type ExceptionCategory,
  type ExceptionSeverity,
  type TransactionStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { StatusBadge } from './TransactionsPage';
import { MonitoringDetailModal, type TransactionDetailDto } from './MonitoringPage';

/**
 * Phase 2G.2 — "What needs attention?" exceptions view.
 *
 * Pure read of `GET /api/exceptions` (admin-only, server-side branch scope).
 * Ages are INFORMATION ONLY — the system defines no deadline or SLA, so
 * nothing here escalates or mutates state; severity comes from the
 * detection rule itself (ERROR = broken invariant, ATTENTION = count
 * discrepancy, INFO = ordinary operational state).
 */
export interface ExceptionItemDto {
  id: string;
  category: ExceptionCategory;
  categoryLabel: string;
  severity: ExceptionSeverity;
  anchorAt: string;
  ageMinutes: number;
  businessDate: string;
  transaction: {
    id: string;
    number: string;
    status: TransactionStatus;
    statusLabel: string;
    fulfillmentMethod: 'PICKUP' | 'DELIVERY';
    deliveryStatus: string | null;
    customerName: string;
    branch: { id: string; code: string };
    createdAt: string;
  };
  detail: Record<string, any>;
}

interface ExceptionsResponse {
  total: number;
  limit: number;
  offset: number;
  generatedAt: string;
  timeZone: string;
  truncated: boolean;
  categories: { id: ExceptionCategory; label: string; hint: string }[];
  severities: { id: ExceptionSeverity; label: string }[];
  categoryCounts: Record<string, number>;
  severityCounts: Record<string, number>;
  items: ExceptionItemDto[];
}

const PAGE_SIZE = 25;

export const SEVERITY_TONE: Record<ExceptionSeverity, 'rose' | 'amber' | 'indigo'> = {
  ERROR: 'rose',
  ATTENTION: 'amber',
  INFO: 'indigo',
};

/**
 * Phase 2G.9 - which detected exceptions can seed a control review case
 * (and against which source). Categories not listed here have no matching
 * case category, so no link is offered rather than a broken one.
 */
const REVIEW_LINKS: Partial<
  Record<
    ExceptionCategory,
    { category: ControlReviewCategory; module: 'TRANSACTION' | 'PROCESSING'; stepId?: (item: ExceptionItemDto) => string | null }
  >
> = {
  PROCESSING_DISCREPANCY: {
    category: 'ITEM_COUNT_DISCREPANCY',
    module: 'PROCESSING',
    stepId: (item) => {
      const first = (item.detail.discrepancies as { processingId?: string }[] | undefined)?.[0];
      return first?.processingId ?? null;
    },
  },
  WORKFLOW_STUCK: {
    category: 'OPEN_OR_STALLED_PROCESSING',
    module: 'PROCESSING',
    // The exception id is `${category}:${transactionId}:${rowId}` for open steps.
    stepId: (item) => item.id.split(':')[2] ?? null,
  },
  OPEN_PROCESSING_STEP: {
    category: 'OPEN_OR_STALLED_PROCESSING',
    module: 'PROCESSING',
    stepId: (item) => item.id.split(':')[2] ?? null,
  },
  WORKFLOW_INCONSISTENCY: {
    category: 'TRANSACTION_WORKFLOW_INCONSISTENCY',
    module: 'TRANSACTION',
  },
  PICKUP_NOT_RELEASED: { category: 'PICKUP_DELIVERY_EXCEPTION', module: 'TRANSACTION' },
  DELIVERY_STEP_DEPTH: { category: 'PICKUP_DELIVERY_EXCEPTION', module: 'TRANSACTION' },
};

/** Deep link into the Control Review page with the case form pre-filled. */
export function controlReviewLink(item: ExceptionItemDto): string | null {
  const target = REVIEW_LINKS[item.category];
  if (!target) return null;
  const params = new URLSearchParams({
    category: target.category,
    module: target.module,
    ref: target.stepId ? (target.stepId(item) ?? item.transaction.id) : item.transaction.id,
    title: `${item.categoryLabel} on ${item.transaction.number}`,
  });
  return `/admin/control-review?${params.toString()}`;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Compact age: 42m / 3h 12m / 4d 6h (raw minutes — no thresholds). */
export function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** Category-specific facts rendered as one compact line. */
function describeFacts(item: ExceptionItemDto): string {
  const d = item.detail;
  switch (item.category) {
    case 'TRANSACTION_AGING':
      return `${d.statusLabel} since ${formatWhen(d.statusSince)}`;
    case 'WORKFLOW_STUCK':
      return `${d.stageLabel} open (window: ${d.requiredStatus}) while ${d.currentStatus}`;
    case 'OPEN_PROCESSING_STEP':
      return `${d.stageLabel}${d.machine ? ` · ${d.machine}` : ''} · started ${formatWhen(d.startedAt)} · ${d.employeeName}`;
    case 'PROCESSING_DISCREPANCY': {
      const list = (d.discrepancies ?? []) as {
        stageLabel: string;
        expectedCount: number;
        actualCount: number;
        difference: number;
      }[];
      if (list.length === 0) return '';
      const first = list[0];
      const extra = list.length > 1 ? ` +${list.length - 1} more` : '';
      return `${first.stageLabel}: expected ${first.expectedCount}, counted ${first.actualCount} (Δ ${first.difference})${extra}`;
    }
    case 'PICKUP_NOT_RELEASED':
      return `Ready since ${formatWhen(d.readyAt)}`;
    case 'DELIVERY_STEP_DEPTH':
      return `Step ${d.depth}/${d.totalSteps} · ${d.deliveryLabel}${d.assignee ? ` · ${d.assignee}` : ''}`;
    case 'ITEM_ACCOUNTABILITY': {
      const mismatch = (d.mismatches ?? [{ source: 'RELEASE' }])[0] as {
        source: string;
        snapshotted?: number;
        difference?: number;
      };
      const what = mismatch.source === 'RELEASE' ? 'released' : 'handed off';
      const snap =
        mismatch.snapshotted !== undefined
          ? `; ${what} ${mismatch.snapshotted} (Δ ${mismatch.difference})`
          : '';
      return `Counted ${d.counted}${snap}`;
    }
    case 'WORKFLOW_INCONSISTENCY': {
      const list = (d.violations ?? []) as { message: string }[];
      if (list.length === 0) return '';
      const extra = list.length > 1 ? ` (+${list.length - 1} more)` : '';
      return `${list[0].message}${extra}`;
    }
    default:
      return '';
  }
}

export function ExceptionsPage() {
  const { branchFilter } = useAuth();

  const [body, setBody] = useState<ExceptionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one is re-resolved server-side on each request.
  const [severityFilter, setSeverityFilter] = useState<'' | ExceptionSeverity>('');
  const [categoryFilter, setCategoryFilter] = useState<'' | ExceptionCategory>('');
  const [statusFilter, setStatusFilter] = useState<'' | TransactionStatus>('');
  const [page, setPage] = useState(0);

  // Shared detail modal (facts + full workflow history), same as monitoring.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TransactionDetailDto | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    if (severityFilter) params.set('severity', severityFilter);
    if (statusFilter) params.set('status', statusFilter);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));

    void api
      .get<ExceptionsResponse>(`/exceptions?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setBody(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load exceptions.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, categoryFilter, severityFilter, statusFilter, page, refreshKey]);

  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    void api
      .get<{ transaction: TransactionDetailDto }>(`/transactions/${detailId}`)
      .then((res) => {
        if (!cancelled) setDetail(res.transaction);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailError(err instanceof ApiError ? err.message : 'Failed to load the transaction.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [detailId, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const total = body?.total ?? 0;
  // The "All" chip must show the full picture even when a severity filter
  // is active (severityCounts ignores its own filter, like the monitoring chips).
  const allCount = body
    ? Object.values(body.severityCounts).reduce((sum, count) => sum + count, 0)
    : 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);

  // Any filter change restarts at the first page.
  const pickSeverity = (value: '' | ExceptionSeverity) => {
    setPage(0);
    setSeverityFilter(value);
  };
  const pickCategory = (value: '' | ExceptionCategory) => {
    setPage(0);
    setCategoryFilter(value);
  };
  const pickStatus = (value: '' | TransactionStatus) => {
    setPage(0);
    setStatusFilter(value);
  };

  return (
    <section className="space-y-4" aria-label="Operational exceptions">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Needs Attention</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · aging, discrepancies and workflow exceptions — detected read-only,
            ordered by severity.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="clock" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          Ages are <span className="font-semibold text-slate-700">information only</span> — this
          system defines no deadline or SLA, so nothing is marked overdue or auto-closed.
          <span className="font-semibold text-slate-700"> ERROR</span> means the stored state
          contradicts a workflow rule; <span className="font-semibold text-slate-700">
          ATTENTION</span> means a count discrepancy needs review.
          {body && ` Detected ${formatWhen(body.generatedAt)} (${body.timeZone}).`}
        </p>
      </Card>

      {error && <ErrorBanner message={error} />}

      {body && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Severity chips — counts stay complete regardless of the active filter. */}
          <button
            type="button"
            onClick={() => pickSeverity('')}
            aria-pressed={severityFilter === ''}
            className={`h-9 rounded-xl px-3 text-sm font-semibold transition-colors ${
              severityFilter === ''
                ? 'bg-accent text-accent-ink'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All · {allCount}
          </button>
          {body.severities.map((severity) => (
            <button
              key={severity.id}
              type="button"
              onClick={() =>
                pickSeverity(severityFilter === severity.id ? '' : severity.id)
              }
              aria-pressed={severityFilter === severity.id}
              className={`h-9 rounded-xl px-3 text-sm font-semibold transition-colors ${
                severityFilter === severity.id
                  ? 'bg-accent text-accent-ink'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {severity.label} · {body.severityCounts[severity.id] ?? 0}
            </button>
          ))}

          <select
            value={categoryFilter}
            onChange={(e) => pickCategory(e.target.value as '' | ExceptionCategory)}
            aria-label="Filter by category"
            className="h-9 rounded-xl border border-slate-300 bg-panel px-2.5 text-sm font-medium text-slate-700"
          >
            <option value="">All categories</option>
            {body.categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.label} · {body.categoryCounts[category.id] ?? 0}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => pickStatus(e.target.value as '' | TransactionStatus)}
            aria-label="Filter by transaction status"
            className="h-9 rounded-xl border border-slate-300 bg-panel px-2.5 text-sm font-medium text-slate-700"
          >
            <option value="">All statuses</option>
            {TRANSACTION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {TRANSACTION_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>
      )}

      {body?.truncated && (
        <Card className="flex items-start gap-3 border-amber-200 bg-amber-50 p-4">
          <Icon name="alert" className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            The active-transaction scan hit its safety cap — the oldest transactions are shown
            first and may not appear here.
          </p>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Detecting exceptions…
        </div>
      ) : !body || body.items.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon name="check" className="h-8 w-8 text-emerald-400" />
          <p className="mt-2 font-medium text-slate-700">
            {categoryFilter || severityFilter || statusFilter
              ? 'No exceptions match these filters.'
              : 'Nothing needs attention right now.'}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {categoryFilter || severityFilter || statusFilter
              ? 'Clear a filter to see the full picture.'
              : 'Aging, discrepancies and stuck steps will appear here as they are detected.'}
          </p>
        </Card>
      ) : (
        <>
          <p className="text-sm text-slate-500">
            Showing {from}–{to} of {total} {total === 1 ? 'exception' : 'exceptions'} · oldest
            first within each severity
          </p>

          {/* Desktop table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Severity</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Transaction</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Age</th>
                  <th className="px-4 py-3">Facts</th>
                  <th className="px-4 py-3" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {body.items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => setDetailId(item.transaction.id)}
                    className="cursor-pointer hover:bg-slate-100/70"
                  >
                    <td className="px-4 py-3">
                      <Badge tone={SEVERITY_TONE[item.severity]}>{item.severity}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{item.categoryLabel}</p>
                      <p className="mt-0.5 max-w-64 text-xs text-slate-500">
                        {EXCEPTION_CATEGORY_HINTS[item.category]}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{item.transaction.number}</p>
                      <p className="text-xs text-slate-500">{item.transaction.customerName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge>{item.transaction.branch.code}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.transaction.status} />
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700">
                      {formatAge(item.ageMinutes)}
                      <p className="text-xs font-normal text-slate-400">{item.businessDate}</p>
                    </td>
                    <td className="max-w-72 px-4 py-3 text-slate-600">{describeFacts(item)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setDetailId(item.transaction.id)}
                        >
                          Review
                        </Button>
                        {controlReviewLink(item) && (
                          <Link
                            to={controlReviewLink(item)!}
                            className="inline-flex h-9 items-center rounded-xl border border-slate-300 bg-panel px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                          >
                            Open case
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          {/* Deterministic pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <Button
                variant="secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <Button
                variant="secondary"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail: loading / error / full modal with the workflow history */}
      {detailId && !detail && !detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading transaction…
          </div>
        </Modal>
      )}
      {detailId && detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <ErrorBanner message={detailError} />
        </Modal>
      )}
      {detail && (
        <MonitoringDetailModal
          tx={detail}
          onClose={() => setDetailId(null)}
          onChanged={(next) => {
            setDetail(next);
            refresh();
          }}
        />
      )}
    </section>
  );
}
